/**
 * Active locale store (ORCH-191)
 *
 * Purpose: The single place every formatter reads the user's locale and clock from.
 *          The formatters in `formatting.ts`, `datetime.ts` and `tableHelpers.tsx` are
 *          plain functions, so a React context cannot reach them; `LocaleProvider`
 *          (`context/LocaleContext.tsx`) writes the resolved values here once per
 *          session and remounts its subtree when they change.
 * Logic: The server resolves the tag from the profile (override → Home Country's
 *        locale → en-US) and returns it on the auth payload; this module only holds
 *        it, validates it against `Intl`, and maps it to a date-fns locale for the
 *        MUI date picker, whose adapter needs a locale object rather than a tag.
 * Dependencies: Intl, date-fns/locale (loaded on demand per locale)
 */

import type { Locale } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';

/** The defensive fallback when nothing resolves: never a design path, just never blank. */
export const DEFAULT_LOCALE = 'en-US';

/** `h12` / `h23` when the user chose a clock; `null` follows the locale. */
export type HourCycle = 'h12' | 'h23' | null;

let activeLocale: string = DEFAULT_LOCALE;
let activeHourCycle: HourCycle = null;

/**
 * Validate a BCP 47 tag with Intl
 *
 * @param tag - A candidate tag ('de-DE', 'ar-EG-u-nu-latn')
 * @returns The canonical tag, or null when Intl rejects it or it is blank
 */
export const canonicalLocale = (tag: string | null | undefined): string | null => {
  if (!tag || typeof tag !== 'string') return null;
  try {
    const [canonical] = Intl.getCanonicalLocales(tag.trim());
    return canonical ?? null;
  } catch {
    return null;
  }
};

/** Normalise a server/user-supplied clock value to `h12`, `h23` or null. */
export const normalizeHourCycle = (value: unknown): HourCycle =>
  value === 'h12' || value === 'h23' ? value : null;

/** The tag every default-locale formatter call uses right now. */
export const getActiveLocale = (): string => activeLocale;

/** The clock every time formatter uses right now (null = the locale decides). */
export const getActiveHourCycle = (): HourCycle => activeHourCycle;

/**
 * Set the active locale and clock
 *
 * Logic: A malformed tag falls back to DEFAULT_LOCALE rather than throwing — Intl
 *        raises RangeError on a bad tag and that must not take the app down.
 *
 * @returns The tag actually applied
 */
export const setActiveLocale = (tag: string | null | undefined, hourCycle?: unknown): string => {
  activeLocale = canonicalLocale(tag) ?? DEFAULT_LOCALE;
  activeHourCycle = normalizeHourCycle(hourCycle);
  return activeLocale;
};

/** Back to the defaults (tests, sign-out). */
export const resetActiveLocale = (): void => {
  activeLocale = DEFAULT_LOCALE;
  activeHourCycle = null;
};

// -----------------------------------------------------------------------------
// date-fns locale for the MUI date picker adapter
// -----------------------------------------------------------------------------

type LocaleLoader = () => Promise<{ default?: Locale } & Record<string, unknown>>;

/**
 * BCP 47 tag → date-fns locale module. Exact tag first ('de-AT'), then the bare
 * language ('de'); anything else uses enUS. Only the picker's typed input and
 * placeholder depend on this — display goes through Intl and is right for every
 * tag — so a missing entry degrades gracefully. Loaded on demand so no locale
 * is bundled until a user needs it. Keys are the names date-fns ships under.
 */
const DATE_FNS_LOADERS: Record<string, LocaleLoader> = {
  'en-US': () => import('date-fns/locale/en-US'),
  'en-GB': () => import('date-fns/locale/en-GB'),
  'en-AU': () => import('date-fns/locale/en-AU'),
  'en-CA': () => import('date-fns/locale/en-CA'),
  'en-IE': () => import('date-fns/locale/en-IE'),
  'en-IN': () => import('date-fns/locale/en-IN'),
  'en-NZ': () => import('date-fns/locale/en-NZ'),
  'en-ZA': () => import('date-fns/locale/en-ZA'),
  de: () => import('date-fns/locale/de'),
  'de-AT': () => import('date-fns/locale/de-AT'),
  fr: () => import('date-fns/locale/fr'),
  'fr-CA': () => import('date-fns/locale/fr-CA'),
  'fr-CH': () => import('date-fns/locale/fr-CH'),
  es: () => import('date-fns/locale/es'),
  it: () => import('date-fns/locale/it'),
  'it-CH': () => import('date-fns/locale/it-CH'),
  pt: () => import('date-fns/locale/pt'),
  'pt-BR': () => import('date-fns/locale/pt-BR'),
  nl: () => import('date-fns/locale/nl'),
  'nl-BE': () => import('date-fns/locale/nl-BE'),
  da: () => import('date-fns/locale/da'),
  sv: () => import('date-fns/locale/sv'),
  nb: () => import('date-fns/locale/nb'),
  fi: () => import('date-fns/locale/fi'),
  pl: () => import('date-fns/locale/pl'),
  cs: () => import('date-fns/locale/cs'),
  sk: () => import('date-fns/locale/sk'),
  hu: () => import('date-fns/locale/hu'),
  ro: () => import('date-fns/locale/ro'),
  bg: () => import('date-fns/locale/bg'),
  el: () => import('date-fns/locale/el'),
  tr: () => import('date-fns/locale/tr'),
  ru: () => import('date-fns/locale/ru'),
  uk: () => import('date-fns/locale/uk'),
  he: () => import('date-fns/locale/he'),
  ar: () => import('date-fns/locale/ar'),
  'ar-DZ': () => import('date-fns/locale/ar-DZ'),
  'ar-EG': () => import('date-fns/locale/ar-EG'),
  'ar-MA': () => import('date-fns/locale/ar-MA'),
  'ar-SA': () => import('date-fns/locale/ar-SA'),
  'ar-TN': () => import('date-fns/locale/ar-TN'),
  hi: () => import('date-fns/locale/hi'),
  id: () => import('date-fns/locale/id'),
  ms: () => import('date-fns/locale/ms'),
  th: () => import('date-fns/locale/th'),
  vi: () => import('date-fns/locale/vi'),
  ja: () => import('date-fns/locale/ja'),
  ko: () => import('date-fns/locale/ko'),
  'zh-CN': () => import('date-fns/locale/zh-CN'),
  'zh-HK': () => import('date-fns/locale/zh-HK'),
  'zh-TW': () => import('date-fns/locale/zh-TW'),
};

const loaded = new Map<string, Locale>([['en-US', enUS]]);

/**
 * The date-fns locale key a tag maps to
 *
 * Logic: Drops any Unicode extension ('ar-EG-u-nu-latn' → 'ar-EG'), tries the
 *        language-region part, then the language, then null.
 */
export const dateFnsLocaleKey = (tag: string): string | null => {
  const base = tag.split('-u-')[0];
  const parts = base.split('-');
  const languageRegion = parts.slice(0, 2).join('-');
  if (DATE_FNS_LOADERS[languageRegion]) return languageRegion;
  if (DATE_FNS_LOADERS[parts[0]]) return parts[0];
  return null;
};

/**
 * Load the date-fns locale for a tag (cached; enUS when there is none)
 */
export const loadDateFnsLocale = async (tag: string): Promise<Locale> => {
  const key = dateFnsLocaleKey(tag);
  if (!key) return enUS;
  const cached = loaded.get(key);
  if (cached) return cached;
  try {
    const mod = await DATE_FNS_LOADERS[key]();
    // date-fns exposes each locale as a named export ('de', 'enGB'); a CJS interop
    // `default` may be the module namespace itself, so only trust it when it is a Locale
    const isLocale = (v: unknown): v is Locale =>
      !!v && typeof v === 'object' && typeof (v as Locale).code === 'string';
    const candidate = isLocale(mod.default) ? mod.default : Object.values(mod).find(isLocale);
    const locale = candidate ?? enUS;
    loaded.set(key, locale);
    return locale;
  } catch {
    return enUS;
  }
};

/** The already-loaded date-fns locale for a tag, enUS until `loadDateFnsLocale` resolves. */
export const getLoadedDateFnsLocale = (tag: string): Locale => {
  const key = dateFnsLocaleKey(tag);
  return (key && loaded.get(key)) || enUS;
};
